import type { OrderItem } from '../types/apiTypes';

export const SCAN_SESSION_KEY_PREFIX = 'orderItems';
export const SCAN_SESSION_BIN_KEY_PREFIX = 'orderItemsBins';
export const SCAN_SESSION_SOURCE_KEY_PREFIX = 'orderItemsSources';
export const SCAN_SESSION_POST_SUBMIT_KEY_PREFIX = 'orderItemsPostSubmit';
export const SCAN_SESSION_DAY_KEY = 'wmsScanSessionDay';
export const SCAN_SESSION_CLEARED_EVENT = 'wms-scan-session-cleared';
export const SINGLE_SKU_SCANNED_SKUS_KEY = 'singleSkuScannedSkus';

export function scanSessionStorageKey(orderId: string | number): string {
  return `${SCAN_SESSION_KEY_PREFIX}${String(orderId)}`;
}

export function scanSessionBinStorageKey(orderId: string | number): string {
  return `${SCAN_SESSION_BIN_KEY_PREFIX}${String(orderId)}`;
}

export function scanSessionSourceStorageKey(orderId: string | number): string {
  return `${SCAN_SESSION_SOURCE_KEY_PREFIX}${String(orderId)}`;
}

export function scanSessionPostSubmitStorageKey(orderId: string | number): string {
  return `${SCAN_SESSION_POST_SUBMIT_KEY_PREFIX}${String(orderId)}`;
}

/**
 * A bundle that left a pick bin, keyed by the pick line the scan satisfied. Quantity is
 * committed on submit, so leaving and resuming a pick keeps the pending change.
 * A null prefix means the bin was not identified (OCR mode) and the server should fall
 * back to the parent's primary pick bin.
 */
export interface BundleBinScan {
  readonly sku: string;
  readonly storageIdPrefix: string | null;
}

export type BundleBinScanMap = Readonly<Record<string, BundleBinScan>>;

/** Component QR scanned during a bundle work order, used to build the return list. */
export interface BundleWorkSourceScan {
  readonly labelId: string;
  readonly sku: string;
  readonly shelfIndex: string | null;
}

export function localDateKey(now: Date = new Date()): string {
  const year = String(now.getFullYear());
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function msUntilNextLocalMidnight(now: Date = new Date()): number {
  const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return Math.max(1, nextMidnight.getTime() - now.getTime());
}

export function unscanOrderItems(items: readonly OrderItem[]): OrderItem[] {
  if (!items.some((item) => item.scanned)) {
    return [...items];
  }
  return items.map((item) => (item.scanned ? { ...item, scanned: false } : item));
}

export function markOrderItemsScanned(items: readonly OrderItem[]): OrderItem[] {
  return items.map((item) => (item.scanned ? item : { ...item, scanned: true }));
}

function isScanSessionItemKey(key: string): boolean {
  if (key === SINGLE_SKU_SCANNED_SKUS_KEY) {
    return true;
  }
  return key.startsWith(SCAN_SESSION_KEY_PREFIX) && key !== SCAN_SESSION_DAY_KEY;
}

export function clearScanSessionKeys(storage: Storage = sessionStorage): void {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && isScanSessionItemKey(key)) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    storage.removeItem(key);
  }
}

/**
 * Drops `orderItems*` and `singleSkuScannedSkus` when this tab's stamped local calendar day is in the past.
 * Missing stamp (first run / mid-day deploy) is treated as today so in-progress scans stay.
 * Returns true when keys were cleared.
 */
export function expireScanSessionIfStale(now: Date = new Date()): boolean {
  const today = localDateKey(now);
  const stamped = sessionStorage.getItem(SCAN_SESSION_DAY_KEY);
  if (stamped === today) {
    return false;
  }
  if (stamped == null) {
    sessionStorage.setItem(SCAN_SESSION_DAY_KEY, today);
    return false;
  }
  clearScanSessionKeys();
  sessionStorage.setItem(SCAN_SESSION_DAY_KEY, today);
  window.dispatchEvent(new Event(SCAN_SESSION_CLEARED_EVENT));
  return true;
}

function parseScannedIds(raw: string | null): string[] | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((id): id is string => typeof id === 'string');
  } catch {
    return null;
  }
}

export function writeScannedItemIds(orderId: string | number, items: readonly OrderItem[]): void {
  if (expireScanSessionIfStale()) {
    return;
  }
  const scannedItemIds = items.filter((item) => item.scanned).map((item) => item.id);
  sessionStorage.setItem(scanSessionStorageKey(orderId), JSON.stringify(scannedItemIds));
}

export function loadScannedItems(
  orderId: string | number,
  items: readonly OrderItem[],
): OrderItem[] {
  expireScanSessionIfStale();
  const scannedItemIds = parseScannedIds(sessionStorage.getItem(scanSessionStorageKey(orderId)));
  if (scannedItemIds == null) {
    return [...items];
  }
  const scannedSet = new Set(scannedItemIds);
  return items.map((item) => ({
    ...item,
    scanned: scannedSet.has(item.id),
  }));
}

export function removeScanSession(orderId: string | number): void {
  sessionStorage.removeItem(scanSessionStorageKey(orderId));
  sessionStorage.removeItem(scanSessionBinStorageKey(orderId));
  sessionStorage.removeItem(scanSessionSourceStorageKey(orderId));
}

function isBundleBinScan(value: unknown): value is BundleBinScan {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.sku === 'string' &&
    record.sku.trim() !== '' &&
    (record.storageIdPrefix === null || typeof record.storageIdPrefix === 'string')
  );
}

function parseBundleBinScans(raw: string | null): BundleBinScanMap {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const map: Record<string, BundleBinScan> = {};
    for (const [itemId, value] of Object.entries(parsed)) {
      if (isBundleBinScan(value)) {
        map[itemId] = { sku: value.sku, storageIdPrefix: value.storageIdPrefix ?? null };
      }
    }
    return map;
  } catch {
    return {};
  }
}

export function writeBundleBinScans(orderId: string | number, scans: BundleBinScanMap): void {
  if (expireScanSessionIfStale()) {
    return;
  }
  const key = scanSessionBinStorageKey(orderId);
  if (Object.keys(scans).length === 0) {
    sessionStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(scans));
}

export function loadBundleBinScans(orderId: string | number): BundleBinScanMap {
  expireScanSessionIfStale();
  return parseBundleBinScans(sessionStorage.getItem(scanSessionBinStorageKey(orderId)));
}

function isBundleWorkSourceScan(value: unknown): value is BundleWorkSourceScan {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.labelId === 'string' &&
    record.labelId.trim() !== '' &&
    typeof record.sku === 'string' &&
    record.sku.trim() !== '' &&
    (record.shelfIndex === null || typeof record.shelfIndex === 'string')
  );
}

function parseBundleWorkSourceScans(raw: string | null): readonly BundleWorkSourceScan[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    const scans: BundleWorkSourceScan[] = [];
    for (const value of parsed) {
      if (isBundleWorkSourceScan(value)) {
        scans.push({
          labelId: value.labelId,
          sku: value.sku,
          shelfIndex: value.shelfIndex ?? null,
        });
      }
    }
    return scans;
  } catch {
    return [];
  }
}

export function writeBundleWorkSourceScans(
  orderId: string | number,
  scans: readonly BundleWorkSourceScan[],
): void {
  if (expireScanSessionIfStale()) {
    return;
  }
  const key = scanSessionSourceStorageKey(orderId);
  if (scans.length === 0) {
    sessionStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(scans));
}

export function loadBundleWorkSourceScans(orderId: string | number): readonly BundleWorkSourceScan[] {
  expireScanSessionIfStale();
  return parseBundleWorkSourceScans(sessionStorage.getItem(scanSessionSourceStorageKey(orderId)));
}

export function writeSingleSkuScannedSkus(skus: ReadonlySet<string>): void {
  if (expireScanSessionIfStale()) {
    return;
  }
  sessionStorage.setItem(SINGLE_SKU_SCANNED_SKUS_KEY, JSON.stringify([...skus]));
}

export function loadSingleSkuScannedSkus(): string[] {
  expireScanSessionIfStale();
  return parseScannedIds(sessionStorage.getItem(SINGLE_SKU_SCANNED_SKUS_KEY)) ?? [];
}
